/**
 * `agents` namespace — user-facing copy for the agents top-level area
 * (the AI-coworker workforce: roster, workspace, create wizard, profiles,
 * activity, schedules, integrations). Auto-registered by the i18n catalog
 * glob (`src/<area>/i18n/en.ts` → ns `agents`). One `key: 'value',` per line.
 */
export const messages = {
  failureCheckUnavailable: 'Could not check for recent failures, so no agent here is marked with an error badge. This is NOT an all-clear — an agent may have failed.',
  // ADR 0138 voice mode (AgentVoicePanel)
  voiceTitle: "Voice",
  voiceDesc: "The voice this agent speaks in during voice mode. Stored on the agent’s profile; used by the spoken reply (streaming text-to-speech).",
  voiceProvider: "Provider",
  voiceProviderDefault: "Host default",
  voiceVoiceId: "Voice ID",
  voiceIdPlaceholder: "provider voice id",
  voiceKey: "API key (BYOK)",
  voiceKeySelect: "Select a stored key…",
  voiceNoKeys: "No keys stored yet — add one on the Keys page, then pick it here.",
  voiceKeysUnknown: "Your stored keys couldn't be read, so this is not an empty list — it is unknown.",
  voiceNeedsKey: "This provider needs your API key, or the spoken reply can’t be generated.",
  voiceSave: "Save voice",
  voiceSaving: "Saving…",
  voiceSaved: "Voice saved.",
  voiceHintDefault: "Use the host’s configured default voice.",
  voiceHintElevenlabs: "Bring your own ElevenLabs key; the voice ID is the ElevenLabs voice id.",
  voiceHintOpenai: "The voice ID is an OpenAI voice (e.g. alloy, verse).",
  voiceHintGoogle: "The voice ID is a Google TTS voice name.",
  voiceHintMinimax: "The managed default provider.",

  // Generic / shared
  agent: 'Agent',
  backToAgents: 'All agents',
  loadingAgent: 'Loading agent…',
  loadingActivity: 'Loading activity…',
  loadingBoard: 'Loading board…',
  none: 'none',
  viewRun: 'view run',

  // Task source taxonomy (TaskSourceChip)
  sourceHuman: 'Human',
  sourceWorkflow: 'Workflow',
  sourceAgent: 'Agent',
  sourceDiscord: 'Discord',
  sourceSchedule: 'Schedule',
  sourceApi: 'API',
  sourceTitleWithLabel: '{{type}}: {{label}}',
  sourceTitleCreatedBy: 'Created by {{type}}',

  // Autonomy meter
  autonomySupervised: 'Supervised',
  autonomyGuided: 'Guided',
  autonomyAutonomous: 'Autonomous',
  autonomySupervisedHelp: 'Proposes for review — heartbeat picks queue as proposals for human sign-off (agents propose, humans dispose).',
  autonomyGuidedHelp: 'Routine picks run immediately; high-priority picks queue as proposals for sign-off.',
  autonomyAutonomousHelp: 'Heartbeat picks start runs immediately.',
  autonomyTitle: 'Autonomy: {{label}} — {{gloss}}',
  autonomyAria: 'Autonomy: {{label}}',
  statusPaused: 'Paused',
  relativeJustNow: 'just now',
  relativeMinutesAgo: '{{minutes}}m ago',
  relativeHoursAgo: '{{hours}}h ago',
  relativeDaysAgo: '{{days}}d ago',

  // Roster row / tile
  openWorkspaceTitle: "Open {{persona}}'s workspace",
  actionReview: 'Review',
  actionViewActivity: 'View activity',
  actionFinishSetup: 'Finish setup',
  actionOpenBoard: 'Open board',
  subWorking: 'Working',
  subWaiting: 'Waiting on a person',
  subError: 'A recent run failed — see what went wrong',
  subNeedsSetup: 'No workflows or board yet — finish setup to give it work',
  subPaused: 'Re-enable to resume board triggers and the heartbeat',
  subNext: 'Next: {{workflow}} · {{label}}',
  subWorkflowFallback: 'workflow',
  subNoSchedule: 'No schedule — runs on demand or from the board',
  workflowCount_one: '{{count}} workflow',
  workflowCount_other: '{{count}} workflows',
  toDoCount: '{{count}} to do',
  waitingCount: '{{count}} waiting',
  checkedAgo: 'checked {{when}}',
  checkNowTitle: 'Run the heartbeat now',
  checkAgentNow: 'Check {{persona}} now',
  chatWithPersona: 'Chat with {{persona}}',

  // Activity feed
  feedPickedUp: '{{persona}} picked up “{{title}}”',
  feedWaiting: '{{persona}} has “{{title}}” waiting on a human',
  feedScheduled: '{{persona}}: {{label}} is scheduled',
  feedWorkflowFallback: 'a workflow',
  feedEmpty: 'No work yet. Create an agent, add a task, or click “Check now” to see its heartbeat pick up work.',
  feedPersonaPhoto: "{{persona}}'s photo",
  feedNewTasks_one: '{{persona}} has {{count}} new task in To Do',
  feedNewTasks_other: '{{persona}} has {{count}} new tasks in To Do',
  recurringTitle: 'Recurring tasks',
  recurringLede: 'Standing work this agent runs on a schedule. Each is a scheduler job (also in the Schedules tab); enabling a perception task needs a Google connection (Admin → Connections).',
  recurringLoadError: 'Could not load recurring tasks.',
  recurringUpdateError: 'Could not update the task.',
  recurringPaused: '{{label}} paused.',
  recurringEnabled: '{{label}} enabled.',
  recurringLastRun: 'Last run {{when}}',
  // WF-COS-4 — a fire that consumed its slot and produced NO run. Shown beside
  // "last run", because on its own "last run" reads as "it is working".
  recurringSkipped: 'Did not run {{when}} — {{reason}}',
  recurringSkipBudget: 'the workspace was over its autonomous-run budget',
  recurringSkipUnresolved: 'the workflow could not be resolved',
  recurringSkipDispatch: 'the run could not be started',
  recurringSkipFeatureDisabled: 'the feature that owns this schedule is turned off for this workspace',
  recurringSkipPlanLapsed: 'your plan no longer includes the feature that owns this schedule',
  recurringSkipActorInactive: 'the person it runs as has been deactivated',
  recurringSkipGoalClosed: 'its goal is finished or past a limit, so nothing more will run for it',
  recurringSkipClientInactive: "its client is paused or archived, or its report no longer exists; it resumes when the client is active again",
  recurringSkipTargetRefused: "its reply could not be posted to its conversation; it resumes when access returns",
  recurringSkipTooFrequent: "it runs more often than hourly; choose a coarser schedule",
  recurringSkipAgentPaused: 'the agent it runs as is paused — resume the agent to resume this',
  recurringSkipPolicyDenied: 'the agent is not allowed to run this workflow (it is on its never list)',
  recurringSkipAwaitingApproval: 'it is waiting for approval — this workflow needs approval for this agent',
  recurringCadenceExhausted: 'Won\u2019t fire again — no future time matches this cadence',
  recurringSkipUnknown: 'no reason was recorded',
  recurringSkipUnrecognized: 'a reason was recorded, but this app does not recognize it',
  recurringNeverRun: 'Never run',
  recurringNext: ' · next {{when}}',
  recurringOn: 'On',
  recurringOff: 'Off',
  recurringPause: 'Pause',
  recurringEnable: 'Enable',
  recurringEmpty: 'No recurring tasks configured.',

  // Hire agent modal
  hireLabel: 'Hire an agent',
  hireEyebrow: 'New agent',
  hireTitle: 'Hire an agent',
  hireLede: "Pick a role and we'll prefill its workflows, instructions, and a suggested name — you finish the details in the wizard.",
  hireRole: 'Role',
  hireCustomRole: 'Custom role…',
  hireCustomRoleBlurb: 'Start from a blank wizard and define everything yourself.',
  hireStartingAutonomy: 'Starting autonomy',
  hireAutonomySupervised: 'Supervised — propose for review',
  hireAutonomyGuided: 'Guided — asks on high-priority',
  hireAutonomyAutonomous: 'Autonomous — run immediately',
  hireContinue: 'Continue',

  // Integrations panel
  intgWorkSources: "Work can arrive on {{persona}}'s board from humans, workflows, other agents, chat tools, schedules, or APIs.",
  intgSimulated: 'Simulated',
  intgDiscordExplainer: "In Discord, {{command}} creates a To Do card on {{persona}}'s board. {{persona}}'s heartbeat then picks it up and runs the matching workflow.",
  intgDiscordPlaceholder: 'Follow up on the renewal',
  intgCreateDiscordTask: 'Create simulated Discord task',
  intgCreateBoardFirst: "Create this agent's board first.",
  intgDiscordCreated: "Created a To Do task on {{persona}}'s board from a simulated Discord command.",
  intgAllSources: 'All task sources',
  intgPreview: 'Preview',
  intgPlanned: 'Planned',
  intgDiscordBlurb: 'Teammates assign work from chat with a slash command.',
  intgSlackBlurb: 'Assign tasks from a Slack channel or DM.',
  intgEmailBlurb: 'Forward an email to create a task.',
  intgWebhookBlurb: 'Create tasks programmatically from your systems.',
  intgOtherAgentsBlurb: 'One agent assigns a task to another.',
  intgSlack: 'Slack',
  intgEmail: 'Email',
  intgWebhook: 'Webhook / API',
  intgOtherAgents: 'Other agents',

  // Connection status panel
  connActs: 'Acts autonomously',
  connActsGuardrails: 'Acts with guardrails',
  connProposes: 'Proposes for review',
  connChecking: 'Checking connection readiness…',
  connNoneTitle: 'No required connections',
  connNoneBody: 'This agent declares no external integrations, so nothing gates its autonomy.',
  connAllConfigured: 'All required connections are configured — {{autonomy}}.',
  connNotReady_one: '{{missing}} of {{total}} required connection not ready — held for review until connected (was “{{declared}}”).',
  connNotReady_other: '{{missing}} of {{total}} required connections not ready — held for review until connected (was “{{declared}}”).',
  connRequiredLabel: 'Required connections',
  connNotConnected: '{{provider}} · not connected',
  connProviderStatus: '{{provider}} · {{status}}',
  connEffective: 'Effective autonomy:',
  connDraftOnly: ' — external actions are draft/recommend only until every connection is configured.',

  // Workflow portfolio panel
  portfolioStarted: 'Started {{workflow}} · ',
  portfolioViewRun: 'View run',
  portfolioEmpty: 'No workflows assigned yet. Assign one from the library below so {{persona}} has work to do.',
  portfolioIntro: "These workflows make up {{persona}}'s {{role}} portfolio — the work this role owns. Each card explains what it does and how it's triggered.",
  portfolioRoleFallback: 'role',
  portfolioLocalDefault: 'Local workflow — assigned to this agent.',
  portfolioLocalOnly: 'Local-only — register on the host before it can run from a board or schedule.',
  portfolioAvailabilityUnknown: 'Couldn’t check whether this workflow is registered on the host. Run now will try anyway.',
  portfolioDemoChip: 'Demo',
  portfolioDemoExplain: 'Sample workflow — its AI steps return canned demo output, not real AI.',
  portfolioDemoUnavailable: 'Can’t run on this deployment: demo steps are turned off for signed-in workspaces.',
  portfolioDemoPickerHidden_one: '{{count}} demo workflow is not listed: demo workflows are unavailable on this deployment.',
  portfolioDemoPickerHidden_other: '{{count}} demo workflows are not listed: demo workflows are unavailable on this deployment.',
  portfolioTriggerManual: 'Manual',
  portfolioTriggerBoard: 'Task board',
  portfolioTriggerSchedule: 'Schedule',
  portfolioRunning: 'Running…',
  portfolioRunNow: 'Run now',
  portfolioUnassign: 'Unassign',
  portfolioAssignHeading: 'Assign a workflow',
  portfolioChooseWorkflow: 'Choose a workflow from the library…',
  portfolioWorkflowToAssign: 'Workflow to assign',
  portfolioAssignWorkflow: 'Assign workflow',
  portfolioCreateFromTemplate: 'Create from template',

  // Board panel
  boardIntro: "{{persona}}'s board. New work arrives in To Do. Drag a card between lanes (or run the heartbeat from the header) to let {{persona}} pick up the next task.",

  // Instructions panel
  instrSaved: 'Saved.',
  instrUpdated: 'Instructions updated.',
  instrWhatThisDoes: 'What this agent does',
  instrDescriptionHint: "A short description of {{persona}}'s role — shown on the dashboard and overview.",
  instrDescriptionPlaceholder: '**{{persona}}** routes leads and follows up on opportunities.',
  instrRoleDescriptionAria: "{{persona}}'s role description",
  instrInstructions: 'Instructions',
  instrInstructionsHint: 'These instructions shape how {{persona}} behaves when running workflows or replying in chat.',
  instrSecurityNote: "For security, the current instructions aren't shown here — saving replaces them.",
  instrSaveInstructions: 'Save instructions',
  instrSavedInstructions: 'Saved instructions:',
  instrSaveDescription: 'Save',
  instrReadonlyForkable: '{{persona}} runs an installed agent template ({{agentId}}). Its instructions are read-only here.',
  instrForkToCustomize: 'Fork to customize',
  instrReadonlyBuiltIn: '{{persona}} is a built-in example agent — its underlying instructions aren\'t editable here. Create your own agent to write custom instructions from scratch.',
  instrCreateYourOwn: 'Create your own agent',

  // Structured prompt editor
  promptSectionRoleHeading: 'Role',
  promptSectionRoleHint: 'One line — who this agent is and the job it owns.',
  promptSectionResponsibilitiesHeading: 'Responsibilities',
  promptSectionResponsibilitiesHint: 'What it should do — the tasks it owns.',
  promptSectionVoiceHeading: 'Voice',
  promptSectionVoiceHint: 'Tone and style when it writes or replies.',
  promptSectionToolsHeading: 'Tools',
  promptSectionToolsHint: 'Which tools / integrations it may use.',
  promptSectionEscalationHeading: 'Escalation Rules',
  promptSectionEscalationHint: 'When to pause and hand off to a human.',
  promptSectionGuardrailsHeading: 'Guardrails',
  promptSectionGuardrailsHint: 'What it must NOT do.',
  promptSectionExamplesHeading: 'Examples',
  promptSectionExamplesHint: 'Sample situations → the ideal response.',
  promptGuidedHint: 'Fill the sections that apply — empty ones are omitted.',
  promptRawHint: 'Editing the full prompt as Markdown.',
  promptGuidedSections: 'Guided sections',
  promptRawMarkdown: 'Raw Markdown',
  promptSystemPromptAria: 'System prompt (Markdown)',
  promptIntro: 'Intro',
  promptIntroHint: 'Text before the sections.',

  // Install page
  installBack: '← All agents',
  installEyebrow: 'Agents',
  installTitle: 'Install from registry',
  installLede: 'Agent packs available in this host\'s local registry mirror. Most auto-mount at boot — those rows show "Installed".',
  installLoadError: "Couldn't load pack list: {{error}}",
  installNoneTitle: 'No agent packs in the local registry',
  installNoneBody: 'Configure OPENWOP_REGISTRY_URL + restart the host to fetch from the public registry.',
  installBackToAll: 'Back to all agents',
  installInstalled: 'installed',
  installOperatorOnly: 'Installing an agent pack loads code into this host, so it’s an operator action. Ask a superadmin to install the packs you need.',
  installForbidden: 'Installing agent packs requires a superadmin. Ask an operator to install this pack.',
  installSucceeded: '{{name}} installed.',
  installInstalling: 'Installing…',
  installInstall: 'Install',

  // Health panel
  healthVerdictFailed: '{{label}} failed',
  healthVerdictFailedNote: 'Open the Activity tab to see what went wrong.',
  healthVerdictStale: '{{label}} stale',
  healthVerdictStaleNote: 'These have gone quiet and may need a nudge or to be closed out.',
  healthVerdictPending: '{{count}} awaiting approval',
  healthVerdictPendingNote: 'Drafted actions are waiting for your review before they go out.',
  healthVerdictHealthy: 'Healthy',
  healthVerdictHealthyNote: 'Running clean — nothing needs your attention right now.',
  healthVerdictIdle: 'Idle',
  healthVerdictIdleNote: 'No actions drafted yet — metrics fill in as {{persona}} starts working.',
  healthApprovalRateAria: 'Approval rate {{percent}} percent',
  healthApproved: 'approved',
  healthTitle: 'Agent health',
  healthMeta: 'Operating metrics · admin-only · generated {{when}}',
  healthVerdictStrip: '{{label}} — {{note}}',
  healthGroupActions: 'Actions',
  healthPending: 'Pending',
  healthSent: 'Sent',
  healthFailed: 'Failed',
  healthSuppressed: 'Not sent (policy)',
  healthSuppressedHelp: 'Approved, but your workspace policy for this action kind is draft-only, so it was recorded without sending. Not counted as an approval.',
  healthRatesPending: 'Quality rates start tracking once {{persona}} sends its first action.',
  healthApprovalRate: 'Approval rate',
  healthApprovalRateHelp: 'Share of drafted actions you approved.',
  healthEditRate: 'Edited before send',
  healthEditRateHelp: 'Share of approved actions you edited before they went out.',
  healthCited: 'Cited',
  healthCitedHelp: 'Share of actions that carried a source citation.',
  healthFromConnected: 'From connected content',
  healthFromConnectedHelp: 'Share of actions that drew on connected (untrusted) sources.',
  healthGroupCommitments: 'Commitments',
  healthOpen: 'Open',
  healthStale: 'Stale',
  healthCommitmentCitedHelp: 'Share of open commitments backed by a source citation.',
  healthThisAgent: 'this agent',
  healthActionPlural_one: '{{count}} action',
  healthActionPlural_other: '{{count}} actions',
  healthCommitmentPlural_one: '{{count}} commitment',
  healthCommitmentPlural_other: '{{count}} commitments',
  avatarEditTitle: 'Edit profile photo',
  avatarEditAria: "Edit {{persona}}'s profile photo",
  avatarRoleTitle: '{{role}} role',

  // Avatar editor
  avatarErrorNotImage: 'Choose an image file (PNG, JPEG, or WebP).',
  avatarErrorTooBig: 'That image is over 12 MB. Pick a smaller one.',
  avatarErrorProcess: 'Could not process that image.',
  avatarErrorUnsupported: 'Image editing is not supported in this browser.',
  avatarErrorLoad: 'Could not load that image.',
  avatarPhotoHeading: "{{persona}}'s profile photo",
  avatarCloseEditor: 'Close editor',
  avatarZoom: 'Zoom',
  avatarDropHint: 'Drag an image here, or click to choose',
  avatarFormats: 'PNG, JPEG, or WebP · up to 12 MB',
  avatarRemovePhoto: 'Remove photo',
  avatarChooseAnother: 'Choose another',
  avatarSaving: 'Saving…',
  avatarSavePhoto: 'Save photo',

  // Agent templates page
  templatesEyebrow: 'Agents',
  templatesTitle: 'Agent templates',
  templatesLede: 'Reusable persona-driven LLM workers. A named coworker on the <0>Agents</0> page instantiates a template; mention one in chat with <1>@</1> to add it to your active-agents lineup.',
  templatesInstallFromRegistry: 'Install from registry',
  templatesAuthorNew: '+ Author new',
  templatesRetry: 'Retry',
  templatesNoMatchQuery: 'No agents match <0>{{query}}</0>.',
  templatesLoadErrorTitle: "Couldn't load agent templates",
  templatesLoading: 'Loading agent templates…',
  templatesEmptyTitle: 'No agent templates installed yet',
  templatesEmptyBody: 'Install a template from the registry, or author one from scratch, to start building reusable AI coworkers.',
  templatesFilterGroup: 'Filter agent templates',
  templatesFilterLabel: 'Filter templates',
  templatesFilterPlaceholder: 'Filter by name, description, or pack…',
  templatesCountLabel_one: '{{filtered}} of {{total}} template',
  templatesCountLabel_other: '{{filtered}} of {{total}} templates',
  templatesCaption: 'Installed agent templates',
  templatesColTemplate: 'Template',
  templatesColModelClass: 'Model class',
  templatesColPack: 'Pack',
  templatesColTools: 'Tools',
  templatesColSignals: 'Signals',
  templatesDegradedTitle_one: '{{count}} declared capability tier this host does not satisfy — see agent detail.',
  templatesDegradedTitle_other: '{{count}} declared capability tiers this host does not satisfy — see agent detail.',
  templatesDegraded: 'degraded ×{{count}}',
  templatesHandoff: 'handoff',
  templatesConfidence: 'conf ≥ {{value}}',
  templatesToolsCount_one: '{{count}} tool',
  templatesToolsCount_other: '{{count}} tools',
  templatesNoTools: 'No tools',

  // Agent detail page
  detailBack: 'Agent templates',
  detailLoading: 'Loading agent…',
  detailLoadError: "Couldn't load agent: {{error}}",
  detailNotInstalledTitle: 'Agent not installed',
  detailNotInstalled: 'is not installed on this host.',
  detailBackToList: 'Back to the list.',
  detailDeleteConfirm: 'Delete the "{{persona}}" agent? This can\'t be undone.',
  detailForkTitle: "Duplicate this agent's config into a new one you can customize",
  detailFork: 'Fork',
  detailDeleteTitle: 'Permanently delete this user-authored agent',
  detailDeleting: 'Deleting…',
  detailDelete: 'Delete',
  detailNotDeletable: 'Pack-installed agents are not deletable. Fork to customize.',
  detailDeleteFailed: 'Delete failed: {{error}}',
  detailSectionSource: 'Source',
  detailRowPack: 'Pack',
  detailRowAgentId: 'Agent ID',
  detailSectionModel: 'Model + behaviour',
  detailRowModelClass: 'Model class',
  detailRowConfidence: 'Confidence threshold',
  detailConfidenceHint: 'The agent declares decisions below this score as low-confidence.',
  detailRowHandoff: 'Handoff schemas',
  detailHandoffDeclared: 'Declared',
  detailHandoffNotDeclared: 'Not declared',
  detailHandoffHintDeclared: 'The pack declares typed input + output schemas for inter-agent handoff.',
  detailHandoffHintNot: 'Inter-agent handoff falls back to free-form text.',
  detailSectionMemory: 'Memory shape',
  detailMemoryScratchpad: 'Scratchpad',
  detailMemoryConversation: 'Conversation',
  detailMemoryLongTerm: 'Long-term',
  detailMemoryNone: 'The pack does not declare a memory shape; the runtime treats this agent as stateless across turns.',
  detailToolAllowlist: 'Tool allowlist ({{count}})',
  detailNoTools: 'No tools allowlisted — the agent runs as a pure-completion persona without function-call surface.',
  detailSectionDegraded: 'Degraded capability tiers',
  detailDegradedNote: 'This pack declares capability tiers that this host does not satisfy. The agent still dispatches; these features are inert until the host advertises them.',

  // Profile panel
  profileSaved: 'Profile saved.',
  agentChangedReload: 'Someone changed this agent after you opened it. Reload to see their change, then make your edit again.',
  profileLoading: 'Loading profile…',
  // Guardrails (ADR 0493 — folded into the Instructions tab)
  guardrailsHeading: 'Guardrails',
  guardrailsSubhead: 'Limits on what {{persona}} may do on its own.',
  guardrailsLede: 'Limits on what {{persona}} may do on its own. Autonomy is set in Edit.',
  guardrailsNoneTitle: 'No guardrails yet',
  guardrailsNoneBody: '{{persona}} has no guardrails. Add forbidden actions, approvals, escalation contacts, required connections, and success metrics.',
  guardrailsAdd: 'Add guardrails',
  guardrailsAutonomyLevel: 'Autonomy',
  guardrailsAutonomyHint: 'Set in Edit — this is the single autonomy control. Shown here for context.',
  guardrailsAutonomyTitle: 'The agent autonomy level, set via the Edit button in the header.',
  profileSaving: 'Saving…',
  profileSaveProfile: 'Save profile',
  profileEditProfile: 'Edit profile',
  profileSectionAutonomy: 'Autonomy',
  profileWithinPolicy: 'Within-policy actions',
  profileSectionPermissions: 'Permissions',
  profilePermissionsHintEdit: 'One entry per line. Advisory day-1 (display only).',
  profilePermRead: 'Read',
  profilePermWrite: 'Write',
  profilePermNever: 'Never',
  profileSectionHitl: 'Human-in-the-loop & escalation',
  profileAlwaysApproval: 'Always require approval',
  profileAlwaysApprovalEdit: 'Always require approval (HITL)',
  profileEscContacts: 'Escalation contacts',
  profileEscTriggers: 'Escalation triggers',
  profileSectionConnections: 'Required connections',
  profileConnectionsHintEdit: 'Connections provider ids that gate activation.',
  profileProviders: 'Providers',
  profileSectionMetrics: 'Success metrics',
  profileMetrics: 'Metrics',
  profileWithinPolicyHelp: 'Allowlisted actions permitted without approval. Anything off-list falls back to review.',
  profileListHelp: 'One per line (or comma-separated).',

  // Create agent page (raw form)
  newBack: '← All agents',
  newEyebrow: 'Agents',
  newForkTitle: 'Fork agent',
  newAuthorTitle: 'Author new agent',
  newForkLede: "Customize a copy of an existing agent. System prompts aren't projected over the read API — you'll write a new one.",
  newAuthorLede: 'Define a persona, give it a system prompt, and pick a model class. The agent shows up in the @-mention picker for every chat in this tenant.',
  newPersona: 'Persona',
  newPersonaHint: 'Short name. Becomes the @-mention slug + the chat panel label.',
  newPersonaPlaceholder: 'e.g. Code Reviewer',
  newLabel: 'Label',
  newLabelHint: 'Longer display name. Defaults to the persona when blank.',
  newLabelPlaceholder: 'e.g. Diff-aware code review agent',
  newDescription: 'Description',
  newDescriptionHint: 'One-line summary shown in the agents list.',
  newDescriptionPlaceholder: 'What this agent is for and when to use it.',
  newModelClass: 'Model class',
  newModelClassHint: 'Which capability tier this agent expects (chat = general, reasoning = thinking budget, coding = code-aware, extraction = structured-output).',
  newSystemPrompt: 'System prompt',
  newSystemPromptHint: "The agent's personality + behavior contract. Prepended as the system message on every turn routed through this agent.",
  newSystemPromptPlaceholder: 'You are a senior reviewer. Cite file:line for every finding. Bias toward...',
  newToolAllowlist: 'Tool allowlist',
  newToolAllowlistHint: 'Comma-separated capability ids. Empty = pure-completion agent with no function-call surface.',
  newMemoryShape: 'Memory shape',
  newMemoryShapeHint: 'Which memory tiers this agent uses. Inert today on the reference app — surfaced for forward-compat.',
  newMemoryScratchpad: 'Scratchpad',
  newMemoryConversation: 'Conversation',
  newMemoryLongTerm: 'Long-term',
  newConfidenceThreshold: 'Confidence threshold',
  newConfidenceThresholdHint: '0.0-1.0. The agent declares decisions below this score as low-confidence. Leave blank to skip.',
  newSaving: 'Saving…',
  newSaveFork: 'Save fork',
  newCreateAgent: 'Create agent',
  newCancel: 'Cancel',
  newForkSuffixedName: '{{name}} (fork)',
  newErrorPersonaRequired: 'Persona is required.',
  newErrorPromptRequired: 'System prompt is required.',
  newErrorTooManyTools: 'Tool allowlist supports at most 32 entries.',
  newErrorConfidenceRange: 'Confidence threshold must be a number between 0 and 1.',

  // Dashboard page
  dashStep1: 'Create an agent',
  dashStep2: 'Assign workflows',
  dashStep3: 'Tasks arrive on their board',
  dashStep4: 'Heartbeat picks up work',
  dashTitle: 'Digital workforce',
  dashLede: "The named agents on staff — digital twins for company roles, each with workflows, a schedule, and a task board. Manage who's on staff and what they own; anything that needs your decision waits in the Inbox.",
  dashHireAgent: '+ Hire an agent',
  dashLoading: 'Loading your agents…',
  dashEmptyTitle: 'Agents are named digital coworkers',
  dashEmptyBody: 'Like a sales-ops coordinator or a support triager. Give each one a role, workflows, and a task board — then watch the work happen.',
  dashEmptyViewerBody: 'You can view this workspace\'s agents, but adding one needs edit access. Ask a workspace admin.',
  viewerCannotCreateTitle: 'You can\'t create agents here',
  viewerCannotCreateBody: 'You have view access to this workspace. Creating or forking an agent needs edit access — ask a workspace admin.',
  rosterEmptyViewerBody: 'No agents yet. Adding one needs edit access to this workspace — ask a workspace admin.',
  viewerPanelReadOnly: 'You can view this agent. Changing it needs edit access to this workspace — ask a workspace admin.',
  dashCreateFromTemplate: 'Create from template',
  dashLoadingDemo: 'Loading…',
  dashLoadExample: 'Load example agents',
  dashKeyFiguresAria: 'Workforce key figures — click to filter',
  dashFigureOnStaff: 'On staff',
  dashFigureWorking: 'Working now',
  dashFigureWaiting: 'Waiting on you',
  dashFigureReady: 'Idle & ready',
  dashFilterGroup: 'Filter, sort, and view agents',
  dashSearchPlaceholder: 'Search by name or role…',
  dashSearchAria: 'Search agents',
  dashFilterRoleAria: 'Filter by role',
  dashAllRoles: 'All roles',
  dashSortAria: 'Sort agents',
  dashSortAttention: 'Sort: Needs attention',
  dashSortName: 'Sort: Name',
  dashNoMatchTitle: 'No agents match',
  dashNoMatchBody: 'Try clearing the search or filters.',
  dashClearFilters: 'Clear filters',
  dashTiles: 'Tiles',
  dashList: 'List',
  dashPickedUp: '{{persona}} picked up “{{title}}” and started a run.',
  dashNoEligible: '{{persona}} found no eligible To Do tasks ({{reason}}).',
  dashIdle: 'idle',

  // Create wizard
  wizHeartbeatManual: 'Manual only (Check now)',
  wizHeartbeat2m: 'Every 2 minutes',
  wizHeartbeat15m: 'Every 15 minutes',
  wizHeartbeatHourly: 'Hourly',
  wizModelChat: 'Chat — general conversation',
  wizModelReasoning: 'Reasoning — complex multi-step work',
  wizModelCoding: 'Coding — code generation & review',
  wizModelExtraction: 'Extraction — structured data pulls',
  wizStepOf: 'Step {{step}} of 5',
  wizTitle: 'Create an agent',
  wizLede: 'Pick a role, name your coworker, give it a workflow to run, and choose how autonomously it works.',
  wizStep1Title: 'Pick a role',
  wizStep1Lede: 'The name is how teammates assign work — pick a human-like name.',
  wizCustomRole: 'Custom role',
  wizCustomRoleBlurb: 'Define your own role and workflows.',
  wizName: 'Name',
  wizNamePlaceholder: 'e.g. {{example}}',
  wizRoleTitle: 'Role title',
  wizRoleTitlePlaceholder: 'e.g. Sales Ops Assistant',
  wizStep2Title: 'Persona & instructions',
  wizTone: 'Tone',
  wizDecisionStyle: 'Decision style',
  wizEscalation: 'Escalation behavior',
  wizModelClassLabel: 'Model class',
  wizInstructionsLabel: 'Instructions (editable)',
  wizInstructionsHint: 'Auto-generated from the role — edit freely. Use the sections, or switch to raw Markdown.',
  wizStep3Title: 'Assign workflows',
  wizStep3Recommended: 'Recommended for a {{role}}:',
  wizStep3Choose: 'Choose workflows from the library:',
  wizCreateFromTemplate: 'Create from template →',
  wizStep4Title: 'Board & work sources',
  wizCreateBoard: 'Create a task board (lanes: To Do · Working · Waiting on Human · Done)',
  wizHumanTasks: 'Human tasks (always on)',
  wizWorkflowTasks: 'Workflow-created tasks (always on)',
  wizSimulatedDiscord: 'Simulated Discord tasks',
  wizStep5Title: 'Schedule & heartbeat',
  wizHeartbeatHeading: 'Heartbeat',
  wizHeartbeatHint: 'How often {{name}} checks its board for new work.',
  wizStartingAutonomy: 'Starting autonomy',
  wizAutonomyHint: 'Supervised agents propose work for your sign-off; autonomous agents start runs immediately. You can change this later.',
  wizAutonomySupervised: 'Supervised — propose for review',
  wizAutonomyGuided: 'Guided — asks on high-priority work',
  wizAutonomyAutonomous: 'Autonomous — run immediately',
  wizStarterSchedule: 'Starter schedule (optional)',
  wizStarterScheduleCadence: 'Starter schedule cadence',
  wizNoStarterSchedule: 'No starter schedule',
  wizReviewCreate: 'Review & create',
  wizReviewUnnamed: 'Unnamed',
  wizReviewNewAgent: 'New agent',
  wizReviewNoRoleTitle: 'No role title',
  wizReviewWorkflows: 'Workflows',
  wizReviewNoneSelected: 'None selected',
  wizReviewBoard: 'Board',
  wizReviewBoardYes: 'Task board · To Do / Working / Waiting / Done',
  wizReviewBoardNo: 'No board',
  wizReviewSources: 'Sources',
  wizReviewSourceHuman: 'Human tasks',
  wizReviewSourceWorkflow: 'Workflow-created tasks',
  wizReviewSourceDiscord: 'Simulated Discord',
  wizReviewHeartbeat: 'Heartbeat',
  wizReviewInstructionsPreview: 'Instructions preview',
  wizComposedPrompt: 'You are {{name}}, a {{role}}. You are {{tone}}; you are {{decisionStyle}}; you {{escalation}}.',
  wizComposedNameFallback: 'an assistant',
  wizComposedRoleFallback: 'helpful coworker',
  wizHintPickRole: 'Pick a role to continue.',
  wizHintAddName: 'Add a name so teammates can assign work.',
  wizHintAddRoleTitle: 'Add a role title to continue.',
  wizNext: 'Next',
  wizCreating: 'Creating…',
  wizCreateAgentBtn: 'Create agent',
  wizBoardName: "{{name}}'s board",

  // Roster page
  rosterLede: 'Named "digital-twin employee" agents that own a workflow portfolio, grouped into a descriptive org-chart. Bind a roster member to a board on the <0>Boards</0> page to make its To Do column fire that agent\'s workflow.',
  rosterLevelReview: 'review — proposes',
  rosterLevelGuided: 'guided',
  rosterLevelAuto: 'auto — runs',
  rosterEyebrow: 'Roster',
  rosterTitle: 'Roster & Org-Chart',
  rosterAddAgent: 'Add agent',
  rosterHeading: 'Roster',
  rosterByAutonomy: 'Roster by autonomy',
  rosterFigureAgents: 'Agents',
  rosterFigureAuto: 'Run automatically',
  rosterFigureReview: 'Need sign-off',
  rosterEmptyTitle: 'No named agents yet',
  rosterEmptyBody: 'Add a digital-twin employee — a persona that owns a workflow portfolio — with the Hire agent wizard.',
  rosterAddFirst: 'Add your first agent',
  rosterCountFiltered_one: '{{visible}} of {{total}} agent',
  rosterCountFiltered_other: '{{visible}} of {{total}} agents',
  rosterCount_one: '{{visible}} agent',
  rosterCount_other: '{{visible}} agents',
  rosterCaption: 'Standing agent roster',
  rosterColPersona: 'Persona',
  rosterDisabled: ' · disabled',
  rosterColAgent: 'Agent',
  rosterColAutonomy: 'Autonomy',
  rosterSetAutoTitle: 'Switch to auto: heartbeat picks run immediately',
  rosterSetReviewTitle: 'Switch to review: heartbeat picks need human sign-off (Inbox)',
  rosterSetAuto: 'Set auto',
  rosterSetReview: 'Set review',
  rosterColPortfolio: 'Portfolio',
  rosterNoWorkflows: 'no workflows assigned',
  rosterProfileTitle: "View / edit {{persona}}'s governance profile",
  rosterProfile: 'Profile',
  rosterDeletePersona: 'Delete {{persona}}',
  rosterNoViewTitle: 'No agents in this view',
  rosterNoViewBody: 'No agents match the current search or autonomy filter.',
  rosterSearchPlaceholder: 'Search agents…',
  rosterSearchAria: 'Search the roster by name',
  rosterClearFilters: 'Clear filters',
  rosterToggleReview: '{{persona}} now proposes — its picks need your sign-off in the Inbox.',
  rosterToggleAuto: '{{persona}} now runs its picks automatically.',
  rosterOrgHeading: 'Org-chart',
  rosterOrgLede: "Departments + roles + reporting lines over roster members. An org edge is metadata only — it grants no authority. The responsibility roll-up is the union of a department's members' portfolios.",
  rosterOrgEmptyTitle: 'No org-chart yet',
  rosterOrgEmptyBody: 'Build a flat chart from your roster to see departments and the responsibility roll-up (or PUT a structured chart via the API).',
  rosterGenerateChart: 'Generate flat chart from roster',
  rosterRebuildChart: 'Rebuild flat chart from roster',
  rosterFlatDeptName: 'All Agents',
  rosterFlatRoleName: 'Member',
  rosterDeptUnder: 'under {{parent}}',
  rosterMemberCount_one: '{{count}} member',
  rosterMemberCount_other: '{{count}} members',
  rosterNoMembers: 'no members',
  rosterReportsTo: 'reports to {{persona}}',
  rosterResponsibleFor: 'responsible for:',
  rosterNothingYet: 'nothing yet',
  rosterLoadFailedTitle: 'Could not load the org chart',
  rosterLoadFailedBody: 'This is a failed read, not an answer — it does not mean this workspace has no org chart, so building a new flat one is not offered here.',
  rosterRetry: 'Try again',
  rosterRollupUnavailable: 'roll-up unavailable',
  templatesAdvisorFilterUnavailable: 'The advisor filter could not be read, so this list may include agents that are not reusable templates, and the count may be too high.',
  rosterDeleteModalLabel: 'Delete {{persona}}',
  rosterDeleteEyebrow: 'Delete agent',
  rosterDeleteConfirmTitle: 'Delete “{{persona}}”?',
  rosterDeleteConfirmBody: "This removes the agent and its board/schedules. It can't be undone.",
  rosterDeleteAgent: 'Delete agent',
  rosterProfileModalLabel: "{{persona}}'s profile",
  rosterGovernanceProfile: 'Governance profile',
  rosterProfileModalLede: 'Config, permissions, escalation, autonomy, and compliance for this agent.',

  // Workspace page
  wsHeartbeatManual: 'manual',
  wsHeartbeatMinutes: 'every {{minutes}}m',
  wsHeartbeatHours: 'every {{hours}}h',
  wsTabOverview: 'Overview',
  wsTabWorkflows: 'Workflows',
  wsTabBoard: 'Board',
  wsTabSchedules: 'Recurring tasks',
  wsTabInstructions: 'Instructions',
  wsTabKnowledge: 'Knowledge',
  wsTabMemory: 'Memory',
  wsTabEvals: 'Evals',
  wsTabIntegrations: 'Integrations',
  wsTabActivity: 'Activity',
  wsUpdatePinError: 'Could not update pin.',
  wsPinLimit: 'Pinned agents are full (max {{max}}). Unpin one to pin this agent.',
  wsCheckPickedUp: '{{persona}} picked up “{{title}}” and started a run.',
  wsNoEligible: 'No eligible To Do tasks ({{reason}}).',
  wsIdle: 'idle',
  wsDeleteConfirm: "Delete {{persona}}? This removes the agent and its board, schedules, and pending approvals. It can't be undone.",
  wsAvatarUpdated: "Updated {{persona}}'s profile photo.",
  wsAvatarRemoved: "Removed {{persona}}'s profile photo.",
  wsLoadingAgent: 'Loading agent…',
  wsNotFoundTitle: 'Agent not found',
  wsNotFoundBody: 'This coworker may have been removed or the link is out of date.',
  wsBackToAgents: 'Back to agents',
  wsBack: '← All agents',
  wsPinTitle: 'Pin this agent to the sidebar and/or the AI-chat welcome row',
  wsPinned: 'Pinned',
  wsPin: 'Pin',
  wsPinnedToSidebar: 'Pinned to sidebar',
  wsPinToSidebar: 'Pin to sidebar',
  wsPinnedToChat: 'Pinned to AI chat',
  wsPinToChat: 'Pin to AI chat',
  wsHeartbeatTitleAuto: 'A background daemon runs this agent\'s “Check now” automatically on this cadence; you can also trigger it manually.',
  wsHeartbeatTitleManual: 'This agent checks for work when you click “Check now”, plus on any enabled schedule.',
  wsHeartbeatLabel: 'Heartbeat: {{cadence}}',
  wsChecking: 'Checking…',
  wsCheckNow: 'Check now',
  wsCheckNowTitle: 'Run the heartbeat: let {{persona}} pick up the next To Do task and start its workflow.',
  wsAddTask: 'Add task',
  wsRunWorkflow: 'Run workflow',
  wsPause: 'Pause',
  wsResume: 'Resume',
  wsInstructions: 'Instructions',
  wsDelete: 'Delete',
  wsEditDetails: 'Edit',
  wsEditDetailsTitle: "Edit {{persona}}'s name, title, description, and operating settings",
  wsDetailsUpdated: 'Agent details updated.',
  // Edit-details dialog (AgentDetailsEditor)
  editDetailsTitle: 'Edit {{persona}}',
  editDetailsLede: "Update this agent's name, title, description, and how it operates. Instructions, governance, workflows, and schedules have their own tabs.",
  editName: 'Name',
  editNameRequired: 'Name is required.',
  editTitle: 'Title',
  editTitleHelp: 'A short role line shown under the name.',
  editTitlePlaceholder: 'e.g. Sales Execution',
  editDescription: 'What this does',
  editDescriptionHelp: "A plain-language summary of this agent's role.",
  editDescriptionPlaceholder: 'What does {{persona}} do?',
  editHeartbeat: 'Heartbeat cadence',
  editHeartbeatHelp: 'How often this agent automatically checks its board for work.',
  editHbManual: 'Manual only',
  editHbHostDefault: 'Host default (autonomous)',
  editHbOff: 'Off \u2014 manual checks only',
  editHb15m: 'Every 15 minutes',
  editHb30m: 'Every 30 minutes',
  editHb1h: 'Every hour',
  editHb2h: 'Every 2 hours',
  editHb4h: 'Every 4 hours',
  editHb24h: 'Daily',
  editHbCustom: 'Current (custom)',
  editAutonomy: 'Autonomy',
  editAutonomyHelp: 'What the agent may do when its heartbeat picks up work.',
  editSave: 'Save changes',
  editSaving: 'Saving…',
  wsDeleteTitle: 'Delete {{persona}} and its board, schedules, and pending approvals',
  wsLastChecked: 'Last checked {{when}}',
  wsNotChecked: 'Not checked yet',
  wsNextScheduled: ' · next scheduled run: {{label}}',
  wsAdvancedSummary: 'Advanced protocol details',
  wsAdvancedDetail: '{{persona}} runs manifest agent {{agentId}} · roster id {{rosterId}}.',
  wsOpenRawRoster: 'Open raw roster',
  wsNoBoardTitle: 'No task board yet',
  wsNoBoardBody: '{{persona}} has no task board yet.',
  wsNextUp: 'Next up',
  wsHigh: 'High',
  wsPickUpNow: 'Pick up now',
  wsNoPendingTasks: 'No pending tasks.',
  wsAddATask: 'Add a task',
  wsInProgress: 'In progress',
  wsViewRun: 'View run',
  wsNothingRunning: 'Nothing running.',
  wsNextScheduledLabel: 'Next scheduled',
  wsNoSchedule: 'No schedule.',
  wsAddOne: 'Add one',
  wsWhatDoes: 'What {{persona}} does',
  wsRoleNoDescription: '{{label}} — assign a role description in Instructions.',
  wsWorkflowPortfolio: 'Workflow portfolio',
  wsNoWorkflowsYet: 'No workflows yet.',
  wsWorkflowsAssigned: '{{count}} assigned.',
  wsManageWorkflows: 'Manage workflows',
  wsTaskBoard: 'Task board',
  wsLaneCounts: '{{todo}} To Do · {{working}} Working · {{waiting}} Waiting · {{done}} Done',
  wsOpenBoard: 'Open board',
  wsSchedule: 'Schedule',
  wsNoScheduleYet: 'No schedule yet.',
  wsManageSchedules: 'Manage schedules',
  wsSuccessMetrics: 'Success metrics',
  wsSuccessMetricsHint: 'The KPIs this agent is measured on.',
  wsNextSteps: 'Next steps',
  wsNextStepAddTask: "Add a task to {{persona}}'s board.",
  wsNextStepAssign: 'Assign another workflow from the library.',
  wsNextStepSchedule: 'Schedule a workflow to run on a timer.',
  wsNextStepDiscord: 'Connect Discord so teammates can assign {{persona}} work from chat.',

  // Schedules panel copy
  schedulesEmptyBody: '{{persona}} runs a workflow on a cadence — create one below.',
  schedulesHelper: 'Cadence shown in {{tz}}. Schedules fire automatically on this cadence (a background daemon), or immediately with “Run now”.',
  schedulesNoWorkflowsHint: 'Assign {{persona}} a workflow first (its Workflow portfolio), then schedule it here.',

  // Drawer
  drawerTabOverview: 'Overview',
  drawerTabBoard: 'Board',
  drawerTabActivity: 'Activity',
  drawerCadenceManual: 'manual',
  drawerCadenceMinutes: 'every {{minutes}}m',
  drawerCadenceHours: 'every {{hours}}h',
  drawerApprovedRun: '{{persona}} approved — run {{runId}}… started.',
  drawerApprovedCarrying: '{{persona}} approved — carrying it out.',
  drawerClaimError: 'Could not claim the proposal.',
  drawerSentBack: 'Sent back to {{persona}}.',
  drawerRejectError: 'Could not reject the proposal.',
  drawerClose: 'Close',
  drawerQuickLook: '{{persona}} — quick look',
  drawerChecking: 'Checking…',
  drawerCheckNow: 'Check now',
  drawerChat: 'Chat',
  drawerChatTitle: 'Open a chat routed to {{persona}}',
  drawerCloseQuickLook: 'Close quick look',
  drawerWaitingOnYou: 'Waiting on you · {{when}}',
  drawerApprovalsUnavailable: 'Couldn\'t check what\'s waiting on you — approvals are temporarily unavailable.',
  authorEntry: 'Describe your agent — let AI draft it',
  stashedDraftOffer: 'The Agent Author drafted “{{persona}}” for you.',
  stashedDraftApply: 'Apply to form',
  stashedDraftDismiss: 'Dismiss',
  authorPanelHeading: 'Describe your agent',
  authorPanelHint: 'Tell the Agent Author what you need. It drafts the agent from your workspace\u2019s real catalog and creates it DISABLED — you review and enable it on the Agents page.',
  authorClose: 'Close',
  authorLoading: 'Opening the Agent Author\u2026',
  authorWelcome: 'Describe the agent you need in a sentence or two. Some starting points:',
  authorExample1: 'A researcher that watches my competitors and posts a weekly digest to my board',
  authorExample2: 'A support triager that reads new service-desk tickets and drafts replies for review',
  authorExample3: 'A content editor that turns approved briefs into draft posts, review-gated',
  drawerNow: 'now',
  drawerProposesToRun: 'Proposes to run {{workflow}}.',
  drawerApproveResume: 'Approve & resume',
  drawerSendBack: 'Send back',
  drawerInWaitingSince: 'In waiting since {{when}}',
  drawerBlockerFallback: 'A person needs to act on the board before this can move on.',
  drawerBlockerWorkflow: 'Waiting on your review before {{workflow}} can continue.',
  drawerOpenBoard: 'Open board',
  drawerAutonomy: 'Autonomy',
  drawerHeartbeat: 'Heartbeat',
  drawerLastCheck: 'Last check',
  drawerNever: 'never',
  drawerPortfolio: 'Portfolio',
  drawerNextScheduled: 'Next scheduled: {{workflow}} · {{label}}',
  drawerNoBoard: '{{persona}} has no task board yet.',
  drawerOpenFullWorkspace: 'Open full workspace',
  drawerRoleFallback: 'Agent',

  // Activity tab
  activityHeartbeat: 'picked up a task',
  activitySchedule: 'ran on a schedule',
  activityKanban: 'started a workflow from a card',
  activityApproval: 'ran an approved proposal',
  activityStatusCompleted: 'Completed',
  activityStatusFailed: 'Failed',
  activityStatusRunning: 'Running',
  activityStatusSuspended: 'Suspended',
  activityEmpty: 'No runs yet. Click Check now or run a workflow, and {{persona}}\'s activity — with outcomes and timestamps — will appear here.',
  activityRanIn: ' · ran in {{duration}}',
  activityChained: 'chained',
  activityChainedTitle: 'Caused by an upstream trigger',
  activityRunTitle: 'Run {{status}}',
  activityTruncated: "Showing {{persona}}'s most recent activity. Older runs may exist beyond this window.",

  // Empty block (agentUi)
  loading: 'Loading…',
  boardDeleteCardConfirm: 'Delete the card “{{title}}”? This can\'t be undone.',
  boardDeleteCardConfirmNoTitle: 'Delete the card? This can\'t be undone.',
  // Guardrails textarea placeholders (technical identifiers; not translated)
  phWithinPolicy: 'sendReminder\ncreateDraft',
  phPermNever: 'email.send',
  phPermWrite: 'tasks\ndrafts',
  phPermRead: 'crm\ndocs',
  phHitl: 'email.send\npayment.instruction',
  phEscContacts: 'manager@example.com',
  phEscTriggers: 'deal-value-over-threshold',
  phProviders: 'google\nslack',
  phMetrics: 'tickets_resolved\nresponse_latency',
  rosterHireViaWizard: 'Hire an agent',
  boardStartedRunNotice: 'Started a run — dropping a card into a trigger lane fires its workflow.',
  boardReadOnly: 'You can view this board. Adding, editing or moving cards needs edit access.',
  upNextTitle: "Up next",
  upNextHelp: "The order this agent will work in. Select a task to see why it ranks there.",
  upNextNow: "working on this next",
  upNextCount_one: "{{count}} task queued.",
  upNextCount_other: "{{count}} tasks queued.",
  upNextUnavailable: "Couldn't load the work order. It'll retry shortly.",
  upNextHeldTitle: "Held",
  upNextHeldHelp: "The agent won't pick these up again on its own: each one's last run was stopped or failed. Clear the note on a card to release it.",
  heartbeatReason_paused: "the agent is paused",
  heartbeatReason_no_boards: "it has no board",
  heartbeatReason_empty_board: "its To Do lane is empty",
  heartbeatReason_no_runnable_workflow: "no card names a workflow to run",
  heartbeatReason_policy_denied: "its rules don't allow these workflows",
  heartbeatReason_awaiting_approval: "its proposals are waiting for approval",
  heartbeatReason_held: "every card is held after a stopped or failed run; clear the note on a card to release it",
  heartbeatReason_mock_archive: "every card it could take runs a demo mock workflow, which agents don't run on their own",
  heartbeatReason_no_working_lane: "its board has no lane for work in progress",
  heartbeatReason_no_eligible_tasks: "nothing is ready",
  checkHeldNotice: "{{persona}} has nothing to pick up: every To Do card is held after a stopped or failed run. Release a card on the board to let the agent take it again.",
  // ADR 0768 follow-up P1 — the embed's honest "can see" scope (the agent is NOT bound to what is on screen)
  authorCanSee: "available agents, workflows and your roster",
  // ADR 0999 D7 — AgentCompactionPanel (the per-agent lossy opt-in, TOCU-2)
  compactionTitle: "Tool-output compaction",
  compactionDesc: "How tool results are shortened before this agent reads them. By default only spacing is removed and every value is kept.",
  compactionChipLossless: "Lossless",
  compactionChipLossy: "Lossy",
  compactionLossyLabel: "Let this agent read shortened lists (lossy)",
  compactionLossyWarning: "Lossy removes information. Long lists of records keep only their first and last rows, with a marker saying how many were removed, and empty fields are dropped and named. Lists of plain values (ids, tags) and schema or catalog reads are never shortened. Use it only for agents that summarise, not agents that edit what they read.",
  compactionHead: "Rows kept at the start",
  compactionTail: "Rows kept at the end",
  compactionCountInvalid: "Enter a whole number, 0 or more.",
  compactionScopeNote: "Applies to new runs this agent starts. A run already under way keeps the setting it started with, and a run inside another run follows the run that started it.",
  compactionSave: "Save compaction",
  compactionSaving: "Saving…",
  compactionLoading: "Loading the compaction setting…",
  compactionLoadFailed: "The compaction setting could not be loaded.",
  compactionCountHelp: "Leave blank for {{n}}.",
  compactionSaved: "Compaction setting saved. It applies to the next run this agent starts.",
  // ADR 1005 P3c — the agent workspace's Deployments tab (its console strings live in `agent-deployments`).
  wsTabDeployments: "Deployments",
} as const;
